import {
  addToast,
  Button,
  Divider,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { i18n } from "@lingui/core";
import { plural, t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import * as Sentry from "@sentry/react";
import { useState } from "react";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { useClientFeature } from "@/hooks/useClientFeature";
import { api } from "@/lib/api-client";
import { getExitChargeSaleUrl } from "@/pages/Dashboard/CustomerSubscriptionOverview/components/subscriptionInvoiceUtils";
import {
  decidesLaterTerm,
  editableCommitment,
  effectiveRenewalBehavior,
  isPastCommitment,
} from "@/pages/Dashboard/Subscriptions/commitmentPlanTerm";
import { renewalBehaviorItems } from "@/pages/Dashboard/Subscriptions/components/CommitmentPolicyFormFields";
import { TaxRegisterAuthorizationDialog } from "@/pages/Dashboard/TaxRegister/AuthorizationDialog";
import { InvoiceDetailsModal } from "@/pages/Dashboard/TaxRegister/Invoices/InvoiceDetailsModal";
import type {
  CommitmentRenewalBehavior,
  ExitChargeDocumentType,
  GetCustomerSubscription,
  PutCustomerSubscriptionCommitmentEndOfTerm,
} from "@/server-types";
import { getApiErrorMessage } from "@/utils/api-error";
import { formatCurrency } from "../../../../../../../../shared/utils/utils";
import { CustomerSubscriptionCommitmentTerm } from "./CustomerSubscriptionCommitmentTerm";
import { CustomerSubscriptionPastCommitments } from "./CustomerSubscriptionPastCommitments";
import { CustomerSubscriptionSignCommitmentSection } from "./CustomerSubscriptionSignCommitmentSection";
import { signCommitmentBlockedReason } from "./signCommitmentChoice";

type CustomerSubscription =
  GetCustomerSubscription["response"]["customerSubscription"];

type EndOfTermBody = PutCustomerSubscriptionCommitmentEndOfTerm["body"];

export function CommitmentDecisionControls({
  renewalBehavior,
  hasNotice,
  isPending,
  onDecide,
}: {
  renewalBehavior: CommitmentRenewalBehavior;
  hasNotice: boolean;
  isPending: boolean;
  onDecide: (body: EndOfTermBody) => void;
}) {
  // Showing the stored column instead would make the option the operator wants the one
  // already selected, and HeroUI never reports picking that — leaving them no way back.
  const effective = effectiveRenewalBehavior(renewalBehavior, hasNotice);

  return (
    <div className="flex flex-col gap-2">
      <LimeSelect
        label={t`Ob izteku vezave`}
        labelPlacement="outside"
        data-identifier="commitment-renewal-behavior-select"
        isDisabled={isPending}
        items={renewalBehaviorItems()}
        selectedKeys={[effective]}
        onSingleItemChange={(value) => {
          onDecide({
            renewalBehavior: value as CommitmentRenewalBehavior,
            // Picking anything but the outcome a notice forces takes that notice back.
            ...(hasNotice && { noticeGiven: false }),
          });
        }}
      />
      {hasNotice ? (
        <p
          data-identifier="commitment-notice-override"
          className="text-small text-warning"
        >
          <Trans>
            Stranka je odpovedala vezavo, zato je paket nastavljen na zaključek.
            Izberi drugo možnost, da odpoved prekličeš.
          </Trans>
        </p>
      ) : null}
    </div>
  );
}

export function CustomerSubscriptionCommitmentsModal({
  isOpen,
  onClose,
  customerSubscription,
}: {
  isOpen: boolean;
  onClose: () => void;
  customerSubscription: CustomerSubscription;
}) {
  const { commitments, durationValue, durationUnit, commitmentLock } =
    customerSubscription;

  const editable = editableCommitment(
    commitments,
    commitmentLock.isTermRunning,
  );
  const decidesNext = decidesLaterTerm(commitments, editable);

  const { isFeatureEnabled } = useClientFeature();
  const { data: currency } = api.values.useGetCurrency({});
  // The card mounts this modal whether or not it is open, so the catalogue read waits.
  const {
    data: catalogue,
    isLoading: isLoadingCatalogue,
    isError: isCatalogueError,
  } = api.subscription.useGetSubscription(
    customerSubscription.subscriptionId ?? -1,
    { enabled: isOpen && customerSubscription.subscriptionId != null },
  );

  const plans =
    catalogue?.subscriptionVariations.find(
      (variation) => variation.id === customerSubscription.variationId,
    )?.commitmentPlans ?? [];

  const blockedReason = signCommitmentBlockedReason({
    commitmentsEnabled: isFeatureEnabled("subscriptionCommitments"),
    isRecurring: customerSubscription.isRecurring,
    isCofunded: customerSubscription.payerCompanyId != null,
    isTermRunning: commitmentLock.isTermRunning,
    isBilling:
      customerSubscription.status !== "canceled" &&
      customerSubscription.status !== "expired",
    // The server signs only from a package still on offer; the catalogue read does not
    // filter retired ones out, so without this the picker offers a plan it would refuse.
    isPackageOffered: catalogue == null || catalogue.isActive,
  });

  const { mutateAsync: updateEndOfTerm, isPending } =
    api.customer.useUpdateCustomerSubscriptionCommitmentEndOfTerm();

  const { mutateAsync: signCommitment, isPending: isSigning } =
    api.customer.useSignCustomerSubscriptionCommitment();

  const { mutateAsync: issueExitCharge, isPending: isIssuingCharge } =
    api.customer.useIssueCommitmentExitChargeDocument();

  const canIssueDocuments =
    isFeatureEnabled("taxRegister") &&
    isFeatureEnabled("subscriptionsTaxRegisterIntegration");

  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null,
  );
  const { data: posAuthData } = api.cookie.useGetPosToken();
  const isPosAuthenticated = posAuthData?.userId != null;

  async function issueCharge(
    commitmentId: number,
    documentType: ExitChargeDocumentType,
  ) {
    // An invoice needs a payment, which only the till takes; it prints there too.
    if (documentType === "invoice") {
      window.location.href = getExitChargeSaleUrl(
        { customerSubscriptionId: customerSubscription.id, commitmentId },
        window.location.href,
      );
      return;
    }
    try {
      await issueExitCharge({
        customerSubscriptionId: customerSubscription.id,
        commitmentId,
        body: { sendOnEmail: false },
      });
      addToast({
        title: t`Predračun za odstopnino je izdan`,
        color: "success",
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  async function sign(subscriptionCommitmentPlanId: number) {
    try {
      await signCommitment({
        customerSubscriptionId: customerSubscription.id,
        body: { subscriptionCommitmentPlanId },
      });
      addToast({ title: t`Vezava sklenjena`, color: "success" });
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description:
          getApiErrorMessage(error) ?? t`Vezave ni bilo mogoče skleniti.`,
        color: "danger",
      });
    }
  }

  async function decide(body: EndOfTermBody) {
    try {
      await updateEndOfTerm({
        customerSubscriptionId: customerSubscription.id,
        body,
      });
      addToast({ title: t`Vezava posodobljena`, color: "success" });
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description:
          getApiErrorMessage(error) ?? t`Vezave ni bilo mogoče spremeniti.`,
        color: "danger",
      });
    }
  }

  // Newest first: the term being decided is the one an operator opened this for.
  const ordered = [...commitments].sort(
    (a, b) => b.firstPeriodNumber - a.firstPeriodNumber,
  );
  const live = ordered.filter((commitment) => !isPastCommitment(commitment));
  const past = ordered.filter(isPastCommitment);

  return (
    <>
      <LimeModal
        isOpen={isOpen}
        onClose={onClose}
        size="2xl"
        data-identifier="subscription-commitments-modal"
      >
        <ModalContent>
          <ModalHeader>
            <Trans>Vezave</Trans>
          </ModalHeader>
          <ModalBody>
            {live.length > 0 && past.length > 0 ? (
              // Only worth a heading when there is history to tell it apart from.
              <p
                data-identifier="subscription-live-commitments-heading"
                className="text-default-500 text-tiny"
              >
                {plural(live.length, {
                  one: "Trenutna vezava",
                  two: "Trenutni vezavi",
                  few: "Trenutne vezave",
                  other: "Trenutne vezave",
                })}
              </p>
            ) : null}
            {live.map((commitment, index) => (
              <div key={commitment.id} className="flex flex-col gap-3">
                {index > 0 ? <Divider /> : null}
                <CustomerSubscriptionCommitmentTerm
                  commitment={commitment}
                  durationValue={durationValue}
                  durationUnit={durationUnit}
                />
                {editable?.id === commitment.id ? (
                  <>
                    {decidesNext ? (
                      <p
                        data-identifier="commitment-decides-later-term"
                        className="text-default-500 text-small"
                      >
                        <Trans>
                          Tekoča vezava je že podaljšana, zato ta izbira velja
                          za novo vezavo, prikazano zgoraj.
                        </Trans>
                      </p>
                    ) : null}
                    <CommitmentDecisionControls
                      renewalBehavior={commitment.renewalBehavior}
                      hasNotice={commitment.noticeGivenAt != null}
                      isPending={isPending}
                      onDecide={(body) => void decide(body)}
                    />
                  </>
                ) : null}
              </div>
            ))}
            {past.length > 0 ? (
              <>
                {live.length > 0 ? <Divider /> : null}
                <CustomerSubscriptionPastCommitments
                  commitments={past}
                  durationValue={durationValue}
                  durationUnit={durationUnit}
                  onIssueCharge={
                    canIssueDocuments
                      ? (commitmentId, documentType) =>
                          void issueCharge(commitmentId, documentType)
                      : undefined
                  }
                  isIssuing={isIssuingCharge}
                  onOpenDocument={
                    isFeatureEnabled("taxRegister")
                      ? setSelectedDocumentId
                      : undefined
                  }
                />
              </>
            ) : null}
            {ordered.length > 0 ? <Divider /> : null}
            <CustomerSubscriptionSignCommitmentSection
              plans={plans.map((plan) => ({
                id: plan.id,
                periods: plan.periods,
                priceFormatted: formatCurrency({
                  amount: plan.priceCents / 100,
                  currency: currency ?? "",
                  locale: i18n.locale,
                }),
              }))}
              durationValue={durationValue}
              durationUnit={durationUnit}
              blockedReason={blockedReason}
              isLoadingPlans={isLoadingCatalogue}
              hasPlansError={isCatalogueError}
              isPending={isSigning}
              onSign={(planId) => void sign(planId)}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onClose}>
              <Trans>Zapri</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </LimeModal>
      <InvoiceDetailsModal
        isAuthenticated={isPosAuthenticated}
        invoiceId={selectedDocumentId ?? undefined}
        onClose={() => setSelectedDocumentId(null)}
        disableNavigationOnCancel
      />
      {/* Asked for only once a document is wanted, so signing a term never needs a PIN. */}
      {selectedDocumentId != null && !isPosAuthenticated ? (
        <TaxRegisterAuthorizationDialog
          onCancel={() => setSelectedDocumentId(null)}
        />
      ) : null}
    </>
  );
}
